import React from 'react';
import { Container, Grid, Card, CardContent, Typography, Box, Chip } from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import ImportContactsIcon from '@mui/icons-material/ImportContacts';
import FormatListNumberedRtlIcon from '@mui/icons-material/FormatListNumberedRtl';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import Groups2Icon from '@mui/icons-material/Groups2';
import AnimatedImagesIcon from '@mui/icons-material/Image';
import LabelIcon from '@mui/icons-material/Label';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useNavigate } from 'react-router-dom';

function MainAdmin() {
  const navigate = useNavigate();
  
  const cards = [
    { 
      name: 'Cursos', 
      icon: <SchoolIcon sx={{ fontSize: 48 }} />, 
      route: '/admin/courses',
      description: 'Gestiona cursos y contenido educativo',
      color: '#4DABA4',
      bgGradient: 'linear-gradient(135deg, #4DABA4 0%, #88B24F 100%)',
      available: true
    },
    { 
      name: 'Repositorio', 
      icon: <ImportContactsIcon sx={{ fontSize: 48 }} />, 
      route: '/admin/repository',
      description: 'Biblioteca de recursos y materiales',
      color: '#88B24F',
      bgGradient: 'linear-gradient(135deg, #88B24F 0%, #4DABA4 100%)',
      available: true
    },
    { 
      name: 'Cuestionarios', 
      icon: <FormatListNumberedRtlIcon sx={{ fontSize: 48 }} />, 
      route: '/admin/questionaires',
      description: 'Gestiona cursos y contenido educativo',
      color: '#4DABA4',
      bgGradient: 'linear-gradient(135deg, #4DABA4 0%, #88B24F 100%)',
      available: true
    },
    { 
      name: 'Etiquetas', 
      icon: <LabelIcon sx={{ fontSize: 48 }} />, 
      route: '/admin/label-groups',
      description: 'Crea y administra etiquetas y grupos de etiquetas',
      color: '#5BB4A8',
      bgGradient: 'linear-gradient(135deg, #5BB4A8 0%, #6DC5BA 100%)',
      available: true
    },
    { 
      name: 'Banners', 
      icon: <SlideshowIcon sx={{ fontSize: 48 }} />,
      route: '/admin/banners',
      description: 'Administra banners y promociones',
      color: '#6DC5BA',
      bgGradient: 'linear-gradient(135deg, #6DC5BA 0%, #4DABA4 100%)',
      available: true
    },
    { 
      name: 'Publicaciones', 
      icon: <NewspaperIcon sx={{ fontSize: 48 }} />,
      route: '/admin/publicaciones',
      description: 'Crea y gestiona publicaciones',
      color: '#5BB4A8',
      bgGradient: 'linear-gradient(135deg, #5BB4A8 0%, #6DC5BA 100%)',
      available: true
    },
    { 
      name: 'Congresos', 
      icon: <Groups2Icon sx={{ fontSize: 48 }} />,
      description: 'Organiza eventos y congresos',
      color: '#7FD1C9',
      bgGradient: 'linear-gradient(135deg, #7FD1C9 0%, #6DC5BA 100%)',
      available: false
    },
  ];

  const handleCardClick = (card) => {
    if (card.available && card.route) {
      navigate(card.route);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f0f8f7 0%, #e8f5f3 100%)',
        py: 4
      }}
    >
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box textAlign="center" mb={6}>
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(45deg, #4DABA4 30%, #88B24F 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2
            }}
          >
            Panel de Administración
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary"
            sx={{ maxWidth: 600, mx: 'auto', lineHeight: 1.6 }}
          >
            Gestiona y administra todos los aspectos de tu plataforma educativa desde un solo lugar
          </Typography>
        </Box>

        {/* Cards Grid */}
        <Grid container spacing={4} justifyContent="center">
          {cards.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                onClick={() => handleCardClick(card)}
                sx={{
                  height: '100%',
                  cursor: card.available ? 'pointer' : 'not-allowed',
                  opacity: card.available ? 1 : 0.7,
                  position: 'relative',
                  overflow: 'visible',
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 4,
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  '&:hover': card.available ? {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 16px 32px rgba(77,171,164,0.18)',
                    borderColor: `${card.color}55`,
                    '& .card-icon': {
                      transform: 'scale(1.08)',
                      background: `linear-gradient(135deg, ${card.color}22, ${card.color}38)`,
                      color: card.color,
                      borderColor: `${card.color}55`,
                    },
                    '& .card-arrow': {
                      transform: 'translateX(6px)',
                      opacity: 1
                    },
                    '& .card-accent': {
                      opacity: 1,
                      transform: 'scaleX(1)',
                    }
                  } : {}
                }}
              >
                {/* Status Chip */}
                <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 2 }}>
                  <Chip 
                    label={card.available ? 'Disponible' : 'Próximamente'} 
                    size="small"
                    sx={{
                      backgroundColor: card.available ? '#4DABA4' : '#88B24F',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                  />
                </Box>

                {/* Acento inferior (sin tapar el texto) */}
                <Box
                  className="card-accent"
                  sx={{
                    position: 'absolute',
                    left: 16,
                    right: 16,
                    bottom: 0,
                    height: 4,
                    borderRadius: '4px 4px 0 0',
                    background: card.bgGradient,
                    opacity: 0.35,
                    transform: 'scaleX(0.4)',
                    transformOrigin: 'center',
                    transition: 'all 0.3s ease',
                    zIndex: 2,
                  }}
                />

                <CardContent 
                  sx={{ 
                    textAlign: 'center', 
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    position: 'relative',
                    zIndex: 1,
                    backgroundColor: '#fff',
                  }}
                >
                  {/* Icon Container */}
                  <Box
                    className="card-icon"
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                      background: `linear-gradient(135deg, ${card.color}20, ${card.color}40)`,
                      color: card.color,
                      transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                      border: `2px solid ${card.color}30`
                    }}
                  >
                    {card.icon}
                  </Box>

                  {/* Content */}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography 
                      variant="h5" 
                      component="h3"
                      sx={{ 
                        fontWeight: 700,
                        mb: 2,
                        color: '#1e293b'
                      }}
                    >
                      {card.name}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        lineHeight: 1.6,
                        mb: 3,
                        color: '#475569'
                      }}
                    >
                      {card.description}
                    </Typography>
                  </Box>

                  {/* Action Area */}
                  {card.available && (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        color: card.color,
                        fontWeight: 600
                      }}
                    >
                      <Typography variant="body2" sx={{ mr: 1, color: 'inherit', fontWeight: 700 }}>
                        Acceder
                      </Typography>
                      <ArrowForwardIosIcon 
                        className="card-arrow"
                        sx={{ 
                          fontSize: 16,
                          transition: 'all 0.3s ease',
                          opacity: 0.7,
                          color: 'inherit',
                        }} 
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Footer Info */}
        <Box textAlign="center" mt={8}>
          <Typography variant="body2" color="text.secondary">
            ¿Necesitas ayuda? Contacta al equipo de soporte técnico
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default MainAdmin;